import {
  Brand,
  BrandFamily,
  Domains,
  Languages,
  LegalArea,
  TranslationTenant,
} from "@/core/types/types";
import getTopLevelDomain from "@/core/utils/getTopLevelDomain";

const GENERAL_TRUSTPILOT_BUSINESS_ID: string = "5071863800006400051cdbb7";
const GENERAL_COOKIEBOT_ID: string = "dc73c278-0f9f-4b8f-8255-67a04ae44982";
const GENERAL_GTM_ID: string = "GTM-MCC9BPD";
const NORDIC_SIDEBAR_ICONS: string[] = Array(3).fill("checkmark");
const GERMAN_SIDEBAR_ICONS: string[] = Array(3).fill("checkmark-dark-blue");
const DOMAIN: string = getTopLevelDomain();
const FULL_TOP_LEVEL_DOMAIN: string =
  DOMAIN === Domains.UK ? "co." + DOMAIN : DOMAIN;

export interface DomainConfig {
  logo?: string;
  trustPilotBusinessId?: string;
  BackupClaimCheckData?: { url: string };
  CanonicalUrl?: { url: string };
  CustomerPortalURL?: { prod: string; stg: string };
  language?: Languages;
}

export interface BrandConfig {
  cookieBotConfigs: string;
  openGraphLogoSources?: string;
  headerLogoSources: string;
  sidebarLogoSources: string;
  mainLogoUrls: string;
  gtmScripts: string;
  enableBrandFace?: boolean;
  brandFamily?: BrandFamily;
  legalAreas: Record<string, LegalAreaConfig>;
  fileUploadLogoSource?: string;
}

export type LegalAreaConfig = {
  advantageIcons?: string[];
  translationTenant?: TranslationTenant;
  enableTrustPilot?: boolean;
  enableMediaLogos?: boolean;
  enableTrustLogos?: boolean;
  domains: Record<string, DomainConfig>;
};

const getBrandConfiguration = (brand: Brand | string): BrandConfig => {
  return brandConfig[brand] || brandConfig[Brand.DEFAULT];
};

export const getLegalAreaConfig = (
  brand: Brand | string,
  legalArea: LegalArea
): LegalAreaConfig => {
  return (
    getBrandConfiguration(brand).legalAreas[legalArea] ||
    getBrandConfiguration(brand).legalAreas[LegalArea.DEFAULT]
  );
};

export const getDomainData = (
  brand: Brand | string,
  legalArea: LegalArea,
  domain: Domains
): DomainConfig => {
  const domains = getLegalAreaConfig(brand, legalArea)?.domains;
  if (domains && domains[domain] && domain) return domains[domain];
  return domains[Domains.DEFAULT];
};

export const getTrustPilotBusinessId = (
  brand: Brand,
  legalArea: LegalArea,
  domain: Domains
): string => {
  return getBrandConfiguration(brand).legalAreas![legalArea].domains![domain]
    .trustPilotBusinessId!;
};

export const isTrustPilotEnabled = (brand: Brand, legalArea: LegalArea) => {
  return (
    getBrandConfiguration(brand)?.legalAreas![legalArea].enableTrustPilot ??
    false
  );
};

export const isMediaLogosEnabled = (brand: Brand, legalArea: LegalArea) => {
  return (
    getBrandConfiguration(brand)?.legalAreas![legalArea].enableMediaLogos ??
    false
  );
};

export const areTrustLogosEnabled = (brand: Brand, legalArea: LegalArea) => {
  return (
    getBrandConfiguration(brand)?.legalAreas![legalArea].enableTrustLogos ??
    false
  );
};

export const isBrandFaceEnabled = (brand: Brand) => {
  return getBrandConfiguration(brand)?.enableBrandFace === true;
};

export default getBrandConfiguration;

const brandConfig: Record<string, BrandConfig> = {
  allright: {
    cookieBotConfigs: GENERAL_COOKIEBOT_ID,
    openGraphLogoSources: "images/logos/opengraph/allright-logo-og.jpg",
    headerLogoSources: "images/logos/allright/allright-logo.svg",
    sidebarLogoSources: "images/logos/allright/allright-logo-dark.svg",
    mainLogoUrls: `https://allright.${FULL_TOP_LEVEL_DOMAIN}`,
    gtmScripts: GENERAL_GTM_ID,
    legalAreas: {
      apr: {
        advantageIcons: ["trophy-blue", "pig-blue", "chat-blue"],
        translationTenant: TranslationTenant.ALLRIGHT,
        enableMediaLogos: true,
        enableTrustPilot: true,
        domains: {
          de: {
            language: Languages.DE,
            BackupClaimCheckData: {
              url: "https://www.allright.de/fluggastrecht/entschaedigung-form",
            },
            CanonicalUrl: {
              url: "https://www.allright.de/fluggastrecht/check/",
            },
            CustomerPortalURL: {
              prod: "https://cma.flightright.de/",
              stg: "https://cma.stg.flightright.de/",
            },
            trustPilotBusinessId: GENERAL_TRUSTPILOT_BUSINESS_ID,
          },
        },
      },
      mie: {
        advantageIcons: ["trophy-blue", "chat-blue", "pig-blue"],
        enableTrustPilot: false,
        enableMediaLogos: true,
        translationTenant: TranslationTenant.MIETE,
        domains: {
          default: {
            language: Languages.DE,
            BackupClaimCheckData: {
              url: "https://www.allright.de/fluggastrecht/entschaedigung-form",
            },
            CanonicalUrl: {
              url: "https://www.allright.de/fluggastrecht/check/",
            },
            CustomerPortalURL: {
              prod: "https://cma.flightright.de/",
              stg: "https://cma.stg.flightright.de/",
            },
            trustPilotBusinessId: "",
          },
        },
      },
      arb: {
        advantageIcons: ["pig-blue", "quick-blue", "secure-blue"],
        enableTrustPilot: true,
        enableMediaLogos: true,
        translationTenant: TranslationTenant.ARBEITSRECHT,
        domains: {
          default: {
            language: Languages.DE,
            BackupClaimCheckData: {
              url: "https://www.allright.de/fluggastrecht/entschaedigung-form",
            },
            CanonicalUrl: {
              url: "https://www.allright.de/fluggastrecht/check/",
            },
            CustomerPortalURL: {
              prod: "https://cma.flightright.de/",
              stg: "https://cma.stg.flightright.de/",
            },
            trustPilotBusinessId: GENERAL_TRUSTPILOT_BUSINESS_ID,
          },
        },
      },
    },
    brandFamily: BrandFamily.ALLRIGHT,
  },
  flightright: {
    openGraphLogoSources: "images/logos/opengraph/flightright-logo-og.jpg",
    cookieBotConfigs: "5e48c6d5-61de-47de-b344-a741ab06b4f5",
    headerLogoSources: "images/logos/flightright/flightright-logo.svg",
    sidebarLogoSources: "images/logos/flightright/flightright-logo-dark.svg",
    mainLogoUrls: `https://flightright.${FULL_TOP_LEVEL_DOMAIN}`,
    gtmScripts: "GTM-TJ2HBK",
    legalAreas: {
      apr: {
        enableMediaLogos: true,
        enableTrustPilot: true,
        advantageIcons: ["trophy", "pig", "chat"],
        domains: {
          net: {
            language: Languages.EN,
            logo: "allright-apr-net-logo.png",
            trustPilotBusinessId: GENERAL_TRUSTPILOT_BUSINESS_ID,
          },
          de: {
            language: Languages.DE,
            logo: "allright-apr-de-logo.png",
            trustPilotBusinessId: GENERAL_TRUSTPILOT_BUSINESS_ID,
            BackupClaimCheckData: {
              url: "https://www.flightright.de/entschaedigung-form",
            },
            CanonicalUrl: { url: "https://www.flightright.de/check/" },
            CustomerPortalURL: {
              prod: "https://cma.flightright.de/",
              stg: "https://cma.stg.flightright.de/",
            },
          },
          default: {
            language: Languages.EN,
            logo: "allright-apr-de-logo.png",
            trustPilotBusinessId: "57830b680000ff0005922c16",
            BackupClaimCheckData: {
              url: "https://www.flightright.com/compensation-form",
            },
            CanonicalUrl: { url: "https://www.flightright.com/check/" },
            CustomerPortalURL: {
              prod: "https://cma.flightright.de/",
              stg: "https://cma.stg.flightright.de/",
            },
          },
          com: {
            language: Languages.EN,
            logo: "allright-apr-de-logo.png",
            trustPilotBusinessId: "57830b680000ff0005922c16",
            BackupClaimCheckData: {
              url: "https://www.flightright.com/compensation-form",
            },
            CanonicalUrl: { url: "https://www.flightright.com/check/" },
            CustomerPortalURL: {
              prod: "https://cma.flightright.de/",
              stg: "https://cma.stg.flightright.de/",
            },
          },
          uk: {
            language: Languages.EN,
            logo: "allright-apr-de-logo.png",
            trustPilotBusinessId: "542ac38a00006400057aa518",
            BackupClaimCheckData: {
              url: "https://www.flightright.co.uk/compensation-form",
            },
            CanonicalUrl: { url: "https://www.flightright.co.uk/check/" },
            CustomerPortalURL: {
              prod: "https://cma.flightright.de/",
              stg: "https://cma.stg.flightright.de/",
            },
          },
          at: {
            language: Languages.DE,
            logo: "allright-apr-de-logo.png",
            trustPilotBusinessId: "542ac40400006400057aa522",
            BackupClaimCheckData: {
              url: "https://www.flightright.at/entschaedigung-form",
            },
            CanonicalUrl: { url: "https://www.flightright.at/check/" },
            CustomerPortalURL: {
              prod: "https://cma.flightright.de/",
              stg: "https://cma.stg.flightright.de/",
            },
          },
          fr: {
            language: Languages.FR,
            logo: "allright-apr-de-logo.png",
            trustPilotBusinessId: "5424770a00006400057a8da4",
            BackupClaimCheckData: {
              url: "https://www.flightright.fr/indemnisation-form",
            },
            CanonicalUrl: { url: "https://www.flightright.fr/check/" },
            CustomerPortalURL: {
              prod: "https://cma.flightright.de/",
              stg: "https://cma.stg.flightright.de/",
            },
          },
          es: {
            language: Languages.ES,
            logo: "allright-apr-de-logo.png",
            trustPilotBusinessId: "542ac3fe00006400057aa51d",
            BackupClaimCheckData: {
              url: "https://www.flightright.es/indemnizacion-form",
            },
            CanonicalUrl: { url: "https://www.flightright.es/check/" },
            CustomerPortalURL: {
              prod: "https://cma.flightright.de/",
              stg: "https://cma.stg.flightright.de/",
            },
          },
          se: {
            language: Languages.SV,
            logo: "allright-apr-de-logo.png",
            trustPilotBusinessId: "542ac3e900006400057aa51f",
            BackupClaimCheckData: {
              url: "https://www.flightright.se/kompensation-form",
            },
            CanonicalUrl: { url: "https://www.flightright.se/check/" },
            CustomerPortalURL: {
              prod: "https://cma.flightright.de/",
              stg: "https://cma.stg.flightright.de/",
            },
          },
        },
      },
    },
    brandFamily: BrandFamily.FLIGHTRIGHT,
  },
  flugrecht: {
    cookieBotConfigs: "61b98344-a956-4114-bbb5-5a5289fe3b30",
    openGraphLogoSources: "images/logos/opengraph/flugrecht-logo-og.jpg",
    headerLogoSources: "images/logos/flugrecht/flugrecht-logo.svg",
    sidebarLogoSources: "images/logos/flugrecht/flugrecht-logo-dark.svg",
    mainLogoUrls: `https://flugrecht.${FULL_TOP_LEVEL_DOMAIN}`,
    gtmScripts: "GTM-5BDBH4",
    brandFamily: BrandFamily.FLUGRECHT,
    legalAreas: {
      apr: {
        enableMediaLogos: true,
        enableTrustPilot: true,
        advantageIcons: GERMAN_SIDEBAR_ICONS,
        domains: {
          de: {
            trustPilotBusinessId: "5b18ee30346edc000179cfbb",
            BackupClaimCheckData: {
              url: "https://www.flugrecht.de/entschaedigung-form",
            },
            CanonicalUrl: { url: "https://www.flugrecht.de/check/" },
            CustomerPortalURL: {
              prod: "https://cma.flugrecht.de/",
              stg: "https://cma.stg.flightright.de/",
            },
          },
        },
        translationTenant: TranslationTenant.FLUGRECHT,
      },
    },
  },
  flyghjalp: {
    cookieBotConfigs: "75257157-2451-4332-9bdf-4e1f20152915",
    openGraphLogoSources: "images/logos/opengraph/flyghjalp-logo-og.jpg",
    headerLogoSources: "images/logos/flyghjalp/flyghjalp-logo.svg",
    sidebarLogoSources: "images/logos/flyghjalp/flyghjalp-logo-dark.svg",
    mainLogoUrls: `https://flyghjalp.${FULL_TOP_LEVEL_DOMAIN}`,
    gtmScripts: "GTM-KKGW5NX",
    brandFamily: BrandFamily.FLYHJAELP,
    legalAreas: {
      apr: {
        enableMediaLogos: true,
        enableTrustPilot: true,
        advantageIcons: NORDIC_SIDEBAR_ICONS,
        translationTenant: TranslationTenant.FLYHJAELP,
        domains: {
          se: {
            trustPilotBusinessId: "587743d60000ff00059aa50c",
          },
        },
      },
    },
  },
  flyhjaelp: {
    enableBrandFace: true,
    legalAreas: {
      apr: {
        enableMediaLogos: false,
        enableTrustPilot: true,
        advantageIcons: NORDIC_SIDEBAR_ICONS,
        translationTenant: TranslationTenant.FLYHJAELP,
        domains: {
          dk: {
            language: Languages.DA,
            BackupClaimCheckData: {
              url: "https://form.typeform.com/to/YXpB2GUH",
            },
            CanonicalUrl: { url: "https://flyhjaelp.dk/check/" },
            CustomerPortalURL: {
              prod: "https://minsag.flyhjaelp.dk/",
              stg: "https://minsag.stg.flyhjaelp.dk/",
            },
            trustPilotBusinessId: "54a3e9a000006400057c6dd9",
          },
          com: {
            language: Languages.EN,
            BackupClaimCheckData: {
              url: "https://form.typeform.com/to/A4BBVNiz",
            },
            CanonicalUrl: { url: "https://refundmore.com/check/" },
            CustomerPortalURL: {
              prod: "https://mycase.refundmore.com/",
              stg: "https://mycase.stg.refundmore.com/",
            },
            trustPilotBusinessId: "54a3e9a000006400057c6dd9",
          },
          no: {
            language: Languages.NO,
            BackupClaimCheckData: {
              url: "https://form.typeform.com/to/mlDJADnm",
            },
            CanonicalUrl: { url: "https://flyhjelp.no/check/" },
            CustomerPortalURL: {
              prod: "https://minsak.flyhjelp.no/",
              stg: "https://minsak.stg.flyhjelp.no/",
            },
            trustPilotBusinessId: "54a3e9a000006400057c6dd9",
          },
          fi: {
            language: Languages.FI,
            BackupClaimCheckData: {
              url: "https://form.typeform.com/to/ScSy6Ia4",
            },
            CanonicalUrl: { url: "https://lentoapu.fi/check/" },
            CustomerPortalURL: {
              prod: "https://tapaukseni.lentoapu.fi/",
              stg: "https://tapaukseni.stg.lentoapu.fi/",
            },
            trustPilotBusinessId: "54a3e9a000006400057c6dd9",
          },
          se: {
            language: Languages.SV,
            BackupClaimCheckData: {
              url: "https://form.typeform.com/to/vKEjWqNa",
            },
            CanonicalUrl: { url: "https://flyghjalp.se/check/" },
            CustomerPortalURL: {
              prod: "https://mittarende.flyghjalp.se/",
              stg: "https://mittarende.stg.flyghjalp.se/",
            },
            trustPilotBusinessId: "54a3e9a000006400057c6dd9",
          },
        },
      },
    },
    brandFamily: BrandFamily.FLYHJAELP,
    headerLogoSources: "images/logos/flyhjaelp/flyhjaelp-logo.svg",
    openGraphLogoSources: "images/logos/opengraph/flyhjaelp-logo-og.jpg",
    sidebarLogoSources: "images/logos/flyhjaelp/flyhjaelp-logo-dark.svg",
    mainLogoUrls: `https://flyhjaelp.${FULL_TOP_LEVEL_DOMAIN}`,
    gtmScripts: "GTM-MHK3GJZ",
    cookieBotConfigs: "808d3e33-9cf7-45be-942c-aee38972f368",
  },
  flyhjelp: {
    legalAreas: {
      apr: {
        enableMediaLogos: true,
        enableTrustPilot: true,
        translationTenant: TranslationTenant.FLYHJAELP,
        domains: {
          no: {
            trustPilotBusinessId: "588083780000ff00059b10ca",
          },
        },
        advantageIcons: NORDIC_SIDEBAR_ICONS,
      },
    },
    brandFamily: BrandFamily.FLYHJAELP,
    headerLogoSources: "images/logos/flyhjelp/flyhjelp-logo.svg",
    openGraphLogoSources: "images/logos/opengraph/flyhjelp-logo-og.jpg",
    sidebarLogoSources: "images/logos/flyhjelp/flyhjelp-logo-dark.svg",
    mainLogoUrls: `https://flyhjelp.${FULL_TOP_LEVEL_DOMAIN}`,
    gtmScripts: "GTM-NZTNLMK",
    cookieBotConfigs: "7b3e15b1-898d-42c5-bffc-54a2a2fd1844",
  },
  lentoapu: {
    legalAreas: {
      apr: {
        enableMediaLogos: true,
        enableTrustPilot: true,
        translationTenant: TranslationTenant.FLYHJAELP,
        domains: {
          fi: {
            trustPilotBusinessId: "60d2e95a84a9e70001a26f25",
          },
        },
        advantageIcons: NORDIC_SIDEBAR_ICONS,
      },
    },
    brandFamily: BrandFamily.FLYHJAELP,
    headerLogoSources: "images/logos/lentoapu/lentoapu-logo.svg",
    openGraphLogoSources: "images/logos/opengraph/lentoapu-logo-og.jpg",
    sidebarLogoSources: "images/logos/lentoapu/lentoapu-logo-dark.svg",
    mainLogoUrls: `https://lentoapu.${FULL_TOP_LEVEL_DOMAIN}`,
    gtmScripts: "GTM-WGRSQ5V",
    cookieBotConfigs: "29995718-6bdf-4adf-a39e-2535fb5b2218",
  },
  refundmore: {
    legalAreas: {
      apr: {
        enableMediaLogos: true,
        enableTrustPilot: true,
        translationTenant: TranslationTenant.FLYHJAELP,
        domains: {
          com: {
            trustPilotBusinessId: "5c347395dc25ca0001b7c76f",
          },
        },
        advantageIcons: NORDIC_SIDEBAR_ICONS,
      },
    },
    brandFamily: BrandFamily.FLYHJAELP,
    headerLogoSources: "images/logos/refundmore/refundmore-logo.svg",
    openGraphLogoSources: "images/logos/opengraph/refundmore-logo-og.jpg",
    sidebarLogoSources: "images/logos/refundmore/refundmore-logo-dark.svg",
    mainLogoUrls: `https://refundmore.${FULL_TOP_LEVEL_DOMAIN}`,
    gtmScripts: "GTM-PFKBMFW",
    cookieBotConfigs: "c534c4ce-d56b-4ccf-b945-5466ff1c5451",
  },
  freem: {
    brandFamily: BrandFamily.DEFAULT,
    gtmScripts: "",
    cookieBotConfigs: "",
    sidebarLogoSources: "",
    mainLogoUrls: "",
    headerLogoSources: "",
    legalAreas: {
      default: {
        translationTenant: TranslationTenant.DEFAULT,
        domains: {
          default: {},
        },
      },
    },
  },
  [Brand.CHEVALIER]: {
    brandFamily: BrandFamily.CHEVALIER,
    gtmScripts: "GTM-5P66V2G",
    cookieBotConfigs: "37e1c5c7-b866-43da-b84c-0e08a43b44e3",
    sidebarLogoSources: `images/logos/${Brand.CHEVALIER}/chevalier-logo.svg`,
    mainLogoUrls: `https://kanzlei-chevalier.${FULL_TOP_LEVEL_DOMAIN}`,
    headerLogoSources: `images/logos/${Brand.CHEVALIER}/chevalier-logo-reversed.svg`,
    fileUploadLogoSource: `images/logos/${Brand.CHEVALIER}/file-upload.svg`,
    legalAreas: {
      default: {
        translationTenant: TranslationTenant.ARBEITSRECHT,
        domains: {
          default: {},
        },
      },
      arb: {
        advantageIcons: ["pig-petrol", "quick-petrol", "secure-petrol"],
        enableTrustPilot: false,
        enableMediaLogos: true,
        enableTrustLogos: true,
        translationTenant: TranslationTenant.ARBEITSRECHT,
        domains: {
          default: {
            language: Languages.DE,
            BackupClaimCheckData: {
              url: "",
            },
            CanonicalUrl: {
              url: "https://www.kanzlei-chevalier.de/arbeitsrecht/check",
            },
            CustomerPortalURL: {
              prod: "",
              stg: "",
            },
            trustPilotBusinessId: "",
          },
        },
      },
    },
  },
  default: {
    brandFamily: BrandFamily.DEFAULT,
    gtmScripts: "",
    cookieBotConfigs: "",
    sidebarLogoSources: "",
    mainLogoUrls: "",
    headerLogoSources: "",
    legalAreas: {
      default: {
        enableTrustPilot: true,
        enableMediaLogos: true,
        translationTenant: TranslationTenant.DEFAULT,
        domains: {
          default: {},
        },
      },
    },
  },
};
